/**
 * Da resposta da IA para os cards da fila de conferência (spec 009, fase 2).
 *
 * Esta é a única camada do assistente de voz que toca rede: ela pergunta ao
 * banco curado quais medicamentos casam com o que foi ouvido. Toda a decisão
 * — preencher, virar lacuna, recusar valor fora de faixa — continua em
 * `normalize.ts`, que é puro.
 *
 * O contrato com a IA não mudou: usamos os MESMOS alvos de extração que o
 * quadro "o que a IA deve preencher" já usa, só que fixados em código nos
 * quatro documentos do ditado.
 */

import { findBankCandidates, curatedToAIMedication, type Medication } from '@/services/firebase/medications';
import { composeLensObservations, type ComposeObsOptions } from '@/utils/lensObservations';
import {
  composeNotesWithDictated,
  isFillInForm,
  loadNotesDefaults,
  matchNoteTemplates,
  mergeNotes,
  splitNotes,
  withoutRemovedLines,
  withoutTemplateTitles,
} from '@/utils/notesDefaults';
import { hasFixedPeriod, loadTriggers, type TriggerKind, type TriggerMap } from '@/utils/phraseTriggers';
import { buildTextDocDraft, type TextDocDraft, type VoiceTextTemplate } from './textDocs';

/** `kindDiscriminator` das notas da receita de medicamentos (MedicationListEditor). */
export const PRESCRIPTION_NOTES_KIND = 'prescriptionNotesV1';

export interface ResolveVoiceOptions extends ComposeObsOptions {
  /** Textos das notas padrão da receita. Default: as estreladas no navegador. */
  prescriptionNotesDefaults?: string[];
  /**
   * Modelos de nota da clínica (título + texto): os cujo título foi DITO
   * entram nas orientações, sem depender de o modelo devolver o campo.
   */
  prescriptionNoteTemplates?: Array<{ title: string; notes: string }>;
  /** Notas padrão (estrela) do pedido de exames. Default: as do navegador. */
  examNotesDefaults?: string[];
  /** Modelos de nota do pedido de exames — título dito → nota entra. */
  examNoteTemplates?: Array<{ title: string; notes: string }>;
  /** Modelos de TEXTO da cirurgia — título dito → texto entra nas observações. */
  surgeryTextTemplates?: Array<{ title: string; notes: string }>;
  /** Motivos canônicos da Próxima Avaliação (`examChecklist.evaluationReasons`). */
  returnReasons?: string[];
  /** Gatilhos de frase por família. Default: os do navegador (com sementes). */
  triggers?: Partial<Record<TriggerKind, TriggerMap>>;
  /** Modelos de atestado/declaração/laudo da clínica (`textDocs.ts`). Sem eles, o card pede o modelo. */
  textTemplates?: VoiceTextTemplate[];
}

/** `kindDiscriminator` das notas do pedido de exames (ExamListEditor). */
export const EXAM_NOTES_KIND = 'examNotesV1';
import type { ExtractionTargetKey } from '@/utils/extractionTargets';
import type { AIExam, AIMedication, AIStructuredResult } from '@/types/ai';
import {
  examGaps,
  canonicalReturnReason,
  defaultReturnReason,
  isOnlyReturnDeadlinePhrase,
  normalizeReturn,
  parseRelativeMonth,
  returnGaps,
  lensRxGaps,
  medicationGaps,
  normalizeLensRx,
  normalizeSurgery,
  splitSurgeries,
  resolveCatalogMatch,
  surgeryGaps,
  type VoiceEscolha,
  type VoiceGap,
  type VoiceLensRx,
  type VoiceMedicationDraft,
  type VoiceReturnDraft,
  type VoiceSurgeryDraft,
} from './normalize';
import type { ExamFillMap } from '@/utils/encounterAutoFill';
import { toVoiceExamFill } from './examFill';

/** Os alvos do ditado. Fixos em código — o médico não escolhe. */
export const VOICE_TARGETS: ExtractionTargetKey[] = [
  'examesAtendimento',
  'receitaOculos',
  'receitaMedicamentos',
  'solicitacaoExames',
  'solicitacaoCirurgia',
];

/**
 * Linhas de nota que o médico tirou do card (× da etiqueta ou apagadas no
 * "Editar"), normalizadas, por campo. O ditado seguinte no mesmo card não as
 * devolve (`withoutRemovedLines` na fusão da fila). Pedido de 08/10/2026.
 */
export interface VoiceRemovedNotes {
  notes?: string[];
  surgeryNotes?: string[];
}

/** Exame do card. `escolha`: o nome saiu de uma dúvida entre itens da lista da clínica. */
export type VoiceExam = AIExam & { escolha?: VoiceEscolha };

/** O exame que sai do card (ficha, editor): a marca da escolha é só da tela do ditado. */
export function examForRecord(e: VoiceExam): AIExam {
  return { nome: e.nome, justificativa: e.justificativa, cid: e.cid };
}

/**
 * Orientação DITADA que pode ser um modelo de nota da clínica ("orientações
 * de pós-operatório de catarata" × "PÓS-OP CATARATA") — o Jev decide (spec
 * 012, 25/10/2026). Sem `por`: ainda não decidido.
 */
export interface VoiceNotaDita {
  campo: 'notes' | 'surgeryNotes';
  /** A linha como foi ditada. */
  ouvido: string;
  /** Decidido: título e texto do modelo que entrou no lugar da linha. */
  titulo?: string;
  nota?: string;
  por?: 'jev' | 'regra';
}

/**
 * `separado`: o médico pediu este documento em PDF separado. A fila então
 * NÃO funde no card aberto (nem trava como duplicata) — abre outro.
 */
export type VoiceDraft =
  | { kind: 'receitaOculos'; label: string; gaps: VoiceGap[]; lens: VoiceLensRx; separado?: boolean }
  | {
      kind: 'receitaMedicamentos';
      label: string;
      gaps: VoiceGap[];
      items: VoiceMedicationDraft[];
      /** Orientações gerais: padrões (estrela) + o que foi ditado. */
      notes?: string;
      notasDitas?: VoiceNotaDita[];
      tiradas?: VoiceRemovedNotes;
      separado?: boolean;
      /**
       * Receituário escolhido no card (pedido de 08/10/2026): `true` =
       * Controle Especial em 2 vias, `false` = simples. Sem a chave, vale a
       * detecção automática (`isControlledMed`), igual ao card da ficha.
       */
      controlled?: boolean;
      /**
       * Texto do modelo "INTERVALO" da clínica: entra sozinho nas orientações
       * quando o card chega a 2+ colírios (`comIntervalo`). Pedido de 25/10/2026.
       */
      notaIntervalo?: string;
    }
  | {
      /**
       * "Solicitações": exames + retorno + cirurgias no MESMO PDF, como o
       * modal manual imprime (exames → cirurgia). O registro no prontuário
       * continua separado (EXAM_REQUEST + um SURGERY por cirurgia).
       * Decisão do Leopoldo, 16/09/2026.
       */
      kind: 'solicitacaoExames';
      label: string;
      gaps: VoiceGap[];
      exams: VoiceExam[];
      /** "Retorno para mostrar exames até dezembro de 2026" — linha abaixo da tabela. */
      retorno?: VoiceReturnDraft | null;
      surgeries: VoiceSurgeryDraft[];
      /** Orientações do pedido de exames (padrões + modelos ditos + ditado), além da linha de retorno. */
      notes?: string;
      /** Observações/justificativa da cirurgia (modelos de texto ditos + ditado). */
      surgeryNotes?: string;
      notasDitas?: VoiceNotaDita[];
      tiradas?: VoiceRemovedNotes;
      /** Pacotes de solicitação aplicados (spec 013) — só pra mostrar no card. */
      pacotes?: string[];
      separado?: boolean;
    }
  | {
      /**
       * Valores de exame ditados (PIO, AV, refração, biometria…) → módulos do
       * atendimento. Não imprime: entra nos campos assim que nasce e é gravado
       * como ENCOUNTER_EXAMS no "Registrar no prontuário". Pedido de 23/09/2026.
       */
      kind: 'examesAtendimento';
      label: string;
      gaps: VoiceGap[];
      fill: ExamFillMap;
      /**
       * De onde veio o valor, por módulo, quando NÃO foi ditado (ex.:
       * `{ 'pio-sopro': 'lido da foto da tonometria' }`). Aparece no card.
       * Ditar o módulo de novo tira a nota (o valor passou a ser o ditado).
       */
      sources?: Record<string, string>;
      separado?: boolean;
    }
  /**
   * Atestado, declaração ou laudo — um modelo da clínica escolhido pelo que
   * foi dito, com os dias postos (pedido de 08/10/2026). Um card por
   * documento. Ver `textDocs.ts`.
   */
  | TextDocDraft;

/** Rótulo do card de solicitações conforme o que ele carrega. */
export function solicitacoesLabel(d: { exams: AIExam[]; retorno?: VoiceReturnDraft | null; surgeries: VoiceSurgeryDraft[] }): string {
  const temExame = d.exams.length > 0;
  const temCir = d.surgeries.length > 0;
  if (temExame && temCir) return 'Solicitações (exames + cirurgia)';
  if (temCir) return d.retorno ? 'Solicitações (cirurgia + retorno)' : 'Solicitação de cirurgia/procedimento';
  if (temExame) return 'Solicitação de exames';
  return 'Solicitação de retorno';
}

/**
 * Busca no catálogo. Injetável pra que o harness e os testes rodem sem
 * Firestore — em produção é sempre o banco curado real.
 */
export type CandidateLookup = (
  med: AIMedication,
) => Promise<Array<{ med: Medication; score: number }>>;

/** Forma farmacêutica — o que separa "Maxitrol" pomada de "Maxitrol" colírio. */
type MedForm = 'pomada' | 'gel' | 'colirio' | 'oral';

const semAcentoMin = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** Forma do CADASTRO, pela apresentação/via. Desconhecida → null. */
function formOfItem(item: AIMedication): MedForm | null {
  const t = semAcentoMin(`${item.dosagem} ${item.via}`);
  if (/\bpomada\b/.test(t)) return 'pomada';
  if (/\bgel\b/.test(t)) return 'gel';
  if (/\b(comprimidos?|capsulas?|oral)\b/.test(t)) return 'oral';
  if (/\b(solucao|suspensao|colirio|gotas?)\b/.test(t)) return 'colirio';
  return null;
}

/** Palavra dita → formas que ela admite ("gota" vale pra colírio e gel: gel também se pinga). */
const FORMA_DITA: Array<[RegExp, MedForm[]]> = [
  [/\bpomada\b/, ['pomada']],
  [/\bgel\b/, ['gel']],
  [/\b(colirio|solucao|suspensao)\b/, ['colirio']],
  [/\bgotas?\b/, ['colirio', 'gel']],
  [/\b(comprimidos?|capsulas?|via oral)\b/, ['oral']],
];

/**
 * Formas que o ditado admite pra ESTE remédio, ou null se nada foi dito
 * sobre a forma (ou o dito se contradiz). A palavra tem que estar no item
 * ouvido E na transcrição (quando há): posologia que o modelo completou
 * sozinho não decide nada.
 */
function formsHeard(heard: AIMedication, transcricao: string): MedForm[] | null {
  const doItem = semAcentoMin([heard.nome, heard.dosagem, heard.via, heard.posologia].join(' '));
  const fala = semAcentoMin(transcricao);
  let admitidas: MedForm[] | null = null;
  for (const [re, formas] of FORMA_DITA) {
    if (!re.test(doItem) || (fala.trim() && !re.test(fala))) continue;
    admitidas = admitidas ? admitidas.filter((f) => formas.includes(f)) : formas;
  }
  return admitidas && admitidas.length > 0 ? admitidas : null;
}

/**
 * Desempate de cadastros com o MESMO nome, só com o que foi dito ou com o
 * que o próprio médico cadastrou — nunca "o mais provável":
 *
 * 1. Forma dita: "Maxitrol pomada" / "Tobrex colírio" / "Zovirax
 *    comprimido" (no banco global esses nomes existem em duas formas).
 * 2. Versão DA CLÍNICA: "Hyabak" salvo pelo marcador da receita (com a
 *    posologia dele) empatava com o "Hyabak" do banco global e virava
 *    escolha a cada ditado. Só vale com UM cadastro da clínica no empate;
 *    dois da clínica (ex.: pré e pós-op) é escolha clínica, fica no card.
 *
 * O que sobrar empatado continua `multi_match` — o médico escolhe.
 * Pedido de 08/10/2026 ("colírios com o mesmo nome").
 */
function breakSameNameTie<C extends { score: number; item: AIMedication; daClinica: boolean }>(
  cands: C[],
  heard: AIMedication,
  transcricao: string,
): C[] {
  if (cands.length < 2) return cands;
  const topo = Math.max(...cands.map((c) => c.score));
  const empatados = cands.filter((c) => c.score === topo);
  if (empatados.length < 2) return cands;
  let resto = empatados;
  const formas = formsHeard(heard, transcricao);
  if (formas) {
    const naForma = resto.filter((c) => {
      const f = formOfItem(c.item);
      return f !== null && formas.includes(f);
    });
    if (naForma.length > 0) resto = naForma;
  }
  if (resto.length > 1) {
    const daClinica = resto.filter((c) => c.daClinica);
    if (daClinica.length === 1) resto = daClinica;
  }
  return resto.length === empatados.length ? cands : resto;
}

/**
 * A receita sai como está no SISTEMA (receituário da clínica ou banco
 * global): posologia, duração e quantidade do cadastro; o que foi dito só
 * preenche campo que o cadastro deixou vazio. Pra imprimir diferente, o
 * médico muda no "Editar" do card.
 *
 * Pedido do Leopoldo, 09/10/2026: o ditado saiu "Druse 1 cápsula ao dia",
 * o cadastro tem 3 ao dia. Antes (08/10) a posologia dita "de verdade" (dose
 * ou frequência) trocava a do cadastro; agora nada dito troca.
 */
export function fromCadastro(dito: string, doCadastro: string): string {
  return (doCadastro ?? '').trim() || (dito ?? '').trim();
}

/**
 * Resolve UM medicamento ouvido contra o banco curado.
 *
 * Regra da spec: match único ou lacuna. O `enrichMedsWithBank` (usado pela
 * extração por texto) escolhe o top-1 do rerank; aqui um empate no topo vira
 * pendência, porque ninguém confere um ditado com a atenção de quem digitou.
 * Desde 25/10/2026 o "Jev decide" (`jev/escolhas.ts`) resolve esse empate
 * antes de o card entrar na fila, com a marca "Jev" no card.
 *
 * Posologia, duração e quantidade vêm do cadastro (`fromCadastro`); o
 * olho continua o ditado — é do paciente, não do remédio.
 */
async function resolveMedication(
  heard: AIMedication,
  lookup: CandidateLookup,
  transcricao = '',
): Promise<VoiceMedicationDraft> {
  const ranked = await lookup(heard);
  const candidates = ranked.map(({ med, score }) => {
    // Item do banco DA CLÍNICA carrega o AIMedication que o médico salvou
    // (posologia humanizada, fórmula, quantidade) — usa ele direto.
    const fromBank = med.clinicAi ?? curatedToAIMedication(med);
    const item: AIMedication = {
      ...fromBank,
      posologia: fromCadastro(heard.posologia, fromBank.posologia),
      duracao: fromCadastro(heard.duracao, fromBank.duracao),
      quantidade: fromCadastro(heard.quantidade, fromBank.quantidade),
      olho: heard.olho || fromBank.olho,
    };
    // Global: a apresentação vem primeiro — é o que separa as opções de
    // mesmo nome ("Maxitrol — Pomada…" × "Maxitrol — Suspensão…").
    const detalhe = med.clinicAi
      ? [med.clinicAi.dosagem, med.clinicAi.posologia].filter((s) => s?.trim()).join(' — ')
      : [med.presentation, med.activeIngredient].filter((s) => s?.trim()).join(' — ');
    const label = detalhe ? `${med.commercialName} — ${detalhe}` : med.commercialName;
    return { label, score, item, daClinica: Boolean(med.clinicAi) };
  });
  // O mesmo remédio salvo duas vezes (mesmo nome, dosagem e posologia) não é
  // empate — é duplicata do cadastro. Colapsa antes de decidir.
  const vistos = new Set<string>();
  const unicos = candidates.filter((c) => {
    const k = [c.item.nome, c.item.dosagem, c.item.posologia, c.item.formula ?? '']
      .map((s) => (s ?? '').trim().toLowerCase())
      .join('|');
    if (vistos.has(k)) return false;
    vistos.add(k);
    return true;
  });
  const decididos = breakSameNameTie(unicos, heard, transcricao);
  const match = resolveCatalogMatch<AIMedication>(heard.nome, decididos);
  if (match.issue !== 'multi_match') return { match };
  // Empate real: expõe as opções empatadas pra escolha no card.
  const topo = Math.max(...decididos.map((c) => c.score));
  return { match, options: decididos.filter((c) => c.score === topo).map(({ label, item }) => ({ label, item })) };
}

/**
 * A orientação ditada entrou como linha própria? Pode ser um modelo de nota
 * da clínica dito pela metade — o Jev decide (`jev/escolhas.ts`). Só frase
 * curta: instrução longa é do médico, não referência a um modelo.
 */
function notaDita(campo: VoiceNotaDita['campo'], ditado: string, notes: string): VoiceNotaDita[] {
  const d = ditado.trim();
  if (!d || d.split(/\s+/).length > 12) return [];
  return splitNotes(notes).some((l) => l.trim() === d) ? [{ campo, ouvido: d }] : [];
}

// ── Intervalo entre colírios (pedido de 25/10/2026) ──
//
// Receita com dois colírios ou mais sempre leva a orientação do intervalo:
// o texto é o do modelo "INTERVALO" da clínica, e entra sozinho quando o card
// chega a 2 colírios, no mesmo ditado ou somando ditados. Pomada, gel e
// comprimido não contam. Linha tirada no × não volta (`tiradas`).

const COLIRIO = /col[ií]rio|(solu[cç][aã]o|suspens[aã]o|emuls[aã]o).*oft[aá]lmic/i;
const ehColirio = (m: AIMedication) => COLIRIO.test(`${m.nome} ${m.dosagem}`);

/** Modelo de nota do intervalo ("INTERVALO"); o título exato vem antes. */
function modeloIntervalo<T extends { title: string; notes: string }>(modelos: T[]): T | undefined {
  const titulo = (t: T) => semAcentoMin(t.title).trim();
  const candidatos = modelos.filter((t) => /\bintervalo\b/.test(titulo(t)) && t.notes.trim() && !isFillInForm(t.notes));
  return candidatos.find((t) => titulo(t) === 'intervalo') ?? candidatos[0];
}

/** Colírios do card. Item por escolher conta se todas as opções são colírio. */
export function contaColirios(items: VoiceMedicationDraft[]): number {
  return items.filter((i) =>
    i.match.value
      ? ehColirio(i.match.value)
      : i.options?.length
        ? i.options.every((o) => ehColirio(o.item))
        : COLIRIO.test(i.match.heard),
  ).length;
}

/** Card com 2+ colírios ganha a orientação do intervalo (uma vez; o × manda). */
export function comIntervalo(draft: VoiceDraft): VoiceDraft {
  if (draft.kind !== 'receitaMedicamentos' || !draft.notaIntervalo?.trim()) return draft;
  if (contaColirios(draft.items) < 2) return draft;
  const atual = draft.notes ?? '';
  const notes = mergeNotes(atual, withoutRemovedLines(draft.notaIntervalo, draft.tiradas?.notes));
  return notes === atual ? draft : { ...draft, notes };
}

/**
 * Monta os cards da fila a partir do JSON da IA.
 *
 * Só nasce card do que foi realmente ditado: seção vazia não vira card vazio.
 */
export async function resolveVoiceDrafts(
  structured: AIStructuredResult | null | undefined,
  lookup: CandidateLookup = findBankCandidates,
  obsOptions?: ResolveVoiceOptions,
): Promise<VoiceDraft[]> {
  if (!structured) return [];
  const drafts: VoiceDraft[] = [];

  // A transcrição entra pra decidir se a adição foi dita COM olho ("adição só
  // no OD"); sem olho, ela vale pros dois (`propagateAdicao`).
  const transcricao = (structured as { transcricao?: string }).transcricao ?? '';
  const lens = normalizeLensRx(structured.receitaOculos, { transcricao });
  if (lens) {
    // A receita ditada sai com as observações PADRÃO do médico (estrela no
    // modal) mais o que ele ditou ("lentes progressivas"), casado com os
    // chips — igual ao que o modal montaria à mão. Pedido de 17/09/2026.
    lens.observacoes = composeLensObservations(lens.observacoes, obsOptions);
    drafts.push({
      kind: 'receitaOculos',
      label: 'Receita de óculos',
      lens,
      gaps: lensRxGaps(lens),
    });
  }

  const meds = (structured.medicamentos ?? []).filter((m) => m.nome?.trim());
  // Orientação ditada SEM remédio ("anota nas orientações: não suspender e
  // intervalo") também é receita: entra no card aberto (`mergeMedDrafts`) ou
  // abre o card, e o remédio vem noutra fala. Pedido de 25/10/2026.
  const obsReceita = (structured.receitaObservacoes ?? '').trim();
  if (meds.length > 0 || obsReceita) {
    const items = await Promise.all(meds.map((m) => resolveMedication(m, lookup, transcricao)));
    // Orientações = notas padrão (estrela no modal) + o que foi ditado.
    const padroes = obsOptions?.prescriptionNotesDefaults
      ?? loadNotesDefaults(PRESCRIPTION_NOTES_KIND).map((d) => d.notes);
    // Título de modelo dito na fala ("não suspender…") → a nota entra,
    // mesmo que o modelo não tenha devolvido "receitaObservacoes".
    const fala = [transcricao, obsReceita].join(' ');
    const modelos = obsOptions?.prescriptionNoteTemplates ?? [];
    const gatilhos = obsOptions?.triggers?.prescriptionNotes ?? loadTriggers('prescriptionNotes');
    const casados = matchNoteTemplates(fala, modelos, gatilhos).map((t) => t.notes);
    // O título que trouxe o modelo não sai de novo como linha solta.
    const ditado = withoutTemplateTitles(obsReceita, modelos, gatilhos);
    const notes = composeNotesWithDictated(ditado, [...padroes, ...casados]);
    const notasDitas = notaDita('notes', ditado, notes);
    const intervalo = modeloIntervalo(modelos);
    drafts.push({
      kind: 'receitaMedicamentos',
      label: 'Receita de medicamentos',
      items,
      notes,
      ...(notasDitas.length > 0 ? { notasDitas } : {}),
      ...(intervalo ? { notaIntervalo: intervalo.notes } : {}),
      // Só orientação, sem remédio: o card avisa até o remédio chegar.
      gaps: items.length > 0
        ? medicationGaps(items)
        : [{ field: 'medicamentos', label: 'Medicamento', heard: '', issue: 'missing' }],
    });
  }

  // Justificativa e CID saem SEMPRE vazios no ditado (decisão do médico,
  // 14/09/2026). A IA escrevia sozinha coisas como "Rastreamento de diabetes
  // mellitus" — texto clínico que ninguém ditou, que ia no documento assinado
  // e que a ancoragem não confere (ela valida o NOME do exame). Zerar aqui é a
  // segunda camada: mesmo que o modelo devolva, não chega ao papel.
  const exams = (structured.exames ?? [])
    .filter((e) => e.nome !== undefined)
    .map((e) => ({ ...e, justificativa: '', cid: '' }));
  // Retorno sem exame vira "Solicitação de retorno": é um EXAM_REQUEST só
  // com a orientação, exatamente como o card "Próxima Avaliação" faz à mão.
  const opcoesRetorno = {
    canonicalReasons: obsOptions?.returnReasons,
    reasonTriggers: obsOptions?.triggers?.returnReason ?? loadTriggers('returnReason'),
    // Nada casou → padrão DA LISTA, pelo contexto: com exame, "mostrar exames".
    fallbackReason: defaultReturnReason(obsOptions?.returnReasons ?? [], exams.length > 0),
  };
  let retorno = normalizeReturn(structured.retorno, new Date(), opcoesRetorno);
  // O modelo às vezes deixa o prazo solto nas orientações ("trazer o exame
  // mês que vem") sem devolver "retorno". Se a orientação é SÓ prazo e dá
  // pra calcular o mês, vira Próxima avaliação — o mesmo que o médico faria
  // à mão. Decisão de 20/09/2026.
  const obsSolta = (structured.examesObservacoes ?? '').trim();
  if (!retorno?.mes.value && obsSolta && isOnlyReturnDeadlinePhrase(obsSolta)) {
    const mes = parseRelativeMonth(obsSolta);
    if (mes) retorno = normalizeReturn({ motivo: obsSolta, mes }, new Date(), opcoesRetorno);
  }
  // O prazo também vem DENTRO da frase do pedido ("solicitar OCT para o mês
  // que vem") e aí não está em orientação nenhuma. Olha frase a frase da
  // transcrição: só a que fala de exame/retorno E tem prazo vira Próxima
  // avaliação — "usar por 3 meses" numa frase de remédio não conta.
  if (!retorno?.mes.value) {
    const falaToda = (structured as { transcricao?: string }).transcricao ?? '';
    const frase = falaToda
      .split(/[.!?;\n]+/)
      .map((f) => f.trim())
      .find((f) => /\b(exame|exames|solicit|pe[cç]o|pedir|trazer|traga|retorn|voltar|volte|mostrar|reavali|revis)/i.test(f)
        // Frase de INSTRUÇÃO ("enviar o resultado pelo WhatsApp em 1 mês") tem
        // prazo mas não é retorno: fica como orientação.
        && !/\b(envi|mand|agend|marc|lig|whatsapp|telefon|recep)/i.test(f)
        && parseRelativeMonth(f) !== null);
    if (frase) {
      const mes = parseRelativeMonth(frase)!;
      const motivoDito = canonicalReturnReason(frase, opcoesRetorno)
        ?? (obsOptions?.returnReasons?.includes('Retorno para mostrar exames') ? 'Retorno para mostrar exames' : 'Retorno');
      retorno = normalizeReturn({ motivo: motivoDito, mes }, new Date(), opcoesRetorno);
    }
  }
  // Cada cirurgia tem o seu olho e vira o seu registro; mas no papel saem
  // juntas com os exames. A regra da divisão está em `splitSurgeries`.
  const surgeries = splitSurgeries(structured.cirurgias)
    .map(normalizeSurgery)
    .filter((s): s is VoiceSurgeryDraft => s !== null);
  if (exams.length > 0 || retorno || surgeries.length > 0) {
    const card = { exams, retorno, surgeries };
    const fala = (structured as { transcricao?: string }).transcricao ?? '';
    // Notas do pedido de exames: padrões + modelo cujo título foi dito + ditado.
    const examDefaults = obsOptions?.examNotesDefaults
      ?? loadNotesDefaults(EXAM_NOTES_KIND).map((d) => d.notes);
    const examCasados = matchNoteTemplates(
      `${fala} ${structured.examesObservacoes ?? ''}`,
      obsOptions?.examNoteTemplates ?? [],
      obsOptions?.triggers?.examNotes ?? loadTriggers('examNotes'),
    )
      // Nota com prazo embutido ("EM 06 MESES") não entra quando o médico
      // ditou um mês: o papel sairia com dois prazos brigando.
      .filter((t) => !(retorno?.mes.value && hasFixedPeriod(t.title, t.notes)))
      .map((t) => t.notes);
    // Orientação que só repete o prazo já vira a linha de Próxima avaliação:
    // não pode sair duas vezes no mesmo papel (visto em campo, 20/09/2026).
    const obsExames = retorno?.mes.value && isOnlyReturnDeadlinePhrase(structured.examesObservacoes ?? '')
      ? ''
      : structured.examesObservacoes ?? '';
    const notes = exams.length > 0 || retorno
      ? composeNotesWithDictated(obsExames, [...examDefaults, ...examCasados])
      : '';
    // Cirurgia: modelo de texto cujo título foi dito ("contra indicação") + ditado.
    const cirCasados = matchNoteTemplates(
      `${fala} ${structured.cirurgiaObservacoes ?? ''}`,
      obsOptions?.surgeryTextTemplates ?? [],
      obsOptions?.triggers?.surgeryText ?? loadTriggers('surgeryText'),
    ).map((t) => t.notes);
    const surgeryNotes = surgeries.length > 0
      ? composeNotesWithDictated(structured.cirurgiaObservacoes ?? '', cirCasados)
      : '';
    const notasDitas = [
      ...notaDita('notes', obsExames, notes),
      ...notaDita('surgeryNotes', structured.cirurgiaObservacoes ?? '', surgeryNotes),
    ];
    drafts.push({
      kind: 'solicitacaoExames',
      label: solicitacoesLabel(card),
      ...card,
      ...(notes ? { notes } : {}),
      ...(surgeryNotes ? { surgeryNotes } : {}),
      ...(notasDitas.length > 0 ? { notasDitas } : {}),
      gaps: [
        ...examGaps(exams),
        ...returnGaps(retorno),
        ...surgeries.flatMap((s, i) => surgeryGaps(s, i)),
      ],
    });
  }

  // Valores de exame ditados → card "Exames do atendimento". Vem antes da
  // ancoragem, que tira o módulo que a fala não sustenta.
  const examFill = toVoiceExamFill(structured.examesAtendimento);
  if (Object.keys(examFill).length > 0) {
    drafts.push({ kind: 'examesAtendimento', label: 'Exames do atendimento', fill: examFill, gaps: [] });
  }

  // Atestado / declaração / laudo: um card por documento, sempre um modelo da
  // clínica (o modelo de voz só diz qual — `textDocs.ts` confere e escolhe).
  for (const pedido of structured.atestadosLaudos ?? []) {
    if (!pedido || typeof pedido.modelo !== 'string' || !(pedido.modelo.trim() || pedido.pedido?.trim())) continue;
    drafts.push(buildTextDocDraft(obsOptions?.textTemplates ?? [], {
      modelo: pedido.modelo.trim() || (pedido.pedido ?? ''),
      pedido: pedido.pedido,
      dias: pedido.dias,
    }));
  }

  // Pedido explícito de PDF separado vale pra tudo que veio NESTE ditado.
  if (structured.separado === true) return drafts.map((d) => ({ ...d, separado: true }));
  return drafts;
}

/**
 * Card completo: não sobrou nenhuma lacuna nele. Lacuna é aviso, não trava —
 * card com pendência imprime igual (pedido do Leopoldo de 09/10/2026: ele
 * corrige e reimprime se quiser); isto só pinta o card e separa o "Só os sem
 * pendência".
 */
export function isDraftPrintable(draft: VoiceDraft): boolean {
  return draft.gaps.length === 0;
}

/** Card que vira papel. "Exames do atendimento" não: vai pros módulos. */
export function isPrintableKind(draft: VoiceDraft): boolean {
  return draft.kind !== 'examesAtendimento';
}

/** Atestado/laudo sem modelo escolhido não tem texto: sairia folha em branco. */
export function hasDraftText(draft: VoiceDraft): boolean {
  return draft.kind !== 'atestadoLaudo' || !!draft.modelo;
}

const vazioField = (f: { value: unknown; heard: string; issue?: unknown }) =>
  f.value === null && !f.heard.trim() && !f.issue;

/**
 * Card sem conteúdo nenhum — é o pré-ativado ("Aguardando ditado") que abre
 * com o ditado (pedido de 26/09/2026). Vazio não imprime, não é registrado,
 * não conta no nº de cards e não vira pendência. Derivado do CONTEÚDO, não
 * de uma marca: o ditado ou o "Editar" que puser qualquer coisa nele o tira
 * desse estado sozinho.
 */
export function isDraftEmpty(draft: VoiceDraft): boolean {
  switch (draft.kind) {
    case 'receitaOculos': {
      const olho = (e: VoiceLensRx['od']) =>
        vazioField(e.esferica) && vazioField(e.cilindrica) && vazioField(e.eixo) && vazioField(e.adicao)
        && !e.dp.trim() && !e.av.trim();
      return olho(draft.lens.od) && olho(draft.lens.oe) && !draft.lens.observacoes.trim();
    }
    case 'receitaMedicamentos':
      return draft.items.length === 0 && !(draft.notes ?? '').trim();
    case 'solicitacaoExames':
      return draft.exams.length === 0 && draft.surgeries.length === 0 && !draft.retorno
        && !(draft.notes ?? '').trim() && !(draft.surgeryNotes ?? '').trim();
    case 'examesAtendimento':
      return Object.keys(draft.fill).length === 0;
    case 'atestadoLaudo':
      return !draft.modelo && !draft.texto.trim() && !draft.pedido.trim() && !draft.opcoes?.length;
  }
}

/**
 * Chave de duplicata: mesmo tipo + mesmo conteúdo normalizado. Dois ditados
 * seguidos do mesmo pedido não devem virar dois documentos iguais.
 */
export function draftSignature(draft: VoiceDraft): string {
  switch (draft.kind) {
    case 'receitaOculos': {
      const eye = (e: VoiceLensRx['od']) =>
        [e.esferica.value, e.cilindrica.value, e.eixo.value, e.adicao.value].join('/');
      return `oculos:${eye(draft.lens.od)}|${eye(draft.lens.oe)}`;
    }
    // As orientações entram na chave: os mesmos remédios ditados de novo COM
    // orientação nova ("… e anota não suspender") não são duplicata — vão pra
    // fusão, que só acrescenta o que falta (visto em campo, 25/10/2026).
    case 'receitaMedicamentos':
      return `meds:${draft.items.map((i) => i.match.value?.nome ?? i.match.heard).sort().join(',').toLowerCase()}` +
        `|notas:${(draft.notes ?? '').trim().toLowerCase()}`;
    case 'solicitacaoExames':
      return `exames:${draft.exams.map((e) => e.nome.trim().toLowerCase()).sort().join(',')}` +
        (draft.retorno ? `|retorno:${draft.retorno.mes.value ?? draft.retorno.mes.heard}` : '') +
        `|cirurgias:${draft.surgeries.map((s) => s.texto.trim().toLowerCase()).sort().join(',')}` +
        `|notas:${(draft.notes ?? '').trim().toLowerCase()}|obs:${(draft.surgeryNotes ?? '').trim().toLowerCase()}`;
    case 'examesAtendimento':
      return `examesAtendimento:${Object.keys(draft.fill).sort().map((m) =>
        `${m}{${Object.keys(draft.fill[m]).sort().map((k) => {
          const c = draft.fill[m][k];
          return `${k}=${c.OD ?? ''}/${c.OE ?? ''}/${c.mono ?? ''}`;
        }).join(',')}}`).join(';')}`;
    case 'atestadoLaudo':
      return `atestadoLaudo:${draft.modelo?.id ?? `?${draft.pedido.trim().toLowerCase()}`}|${draft.dias ?? ''}`;
  }
}
